import { apiClient } from "./api-client";
import { toArray } from "./api-utils";
import type { DynamicFieldSaveRequest } from "./api-types";
import type { PeoplePickerRoster, TriyoDynamicField, TriyoDynamicFieldFile } from "@/types/models";

/** objectType values accepted by /dynamic-fields (mirrors Triyo.Web OBJECT_TYPES). */
export const DYNAMIC_FIELD_OBJECT_TYPES = {
  taskComment: "TaskComment",
  taskDetail: "TaskDetail",
  project: "Project",
} as const;

export const DynamicFieldsApi = {
  /**
   * Dynamic fields configured on a project template. objectId 0 means "create"
   * — the API returns the field definitions with no saved data; a real project
   * id returns the definitions plus that project's values.
   */
  getProjectDynamicFields: async (projectTypeId: number, objectId = 0): Promise<TriyoDynamicField[]> => {
    const res = await apiClient.get<unknown>("/dynamic-fields", {
      params: {
        projectTypeId,
        objectType: DYNAMIC_FIELD_OBJECT_TYPES.project,
        objectId,
      },
    });
    return toArray<TriyoDynamicField>(res.data, "DynamicFields", "dynamicFields");
  },

  /**
   * Dynamic fields configured on a task type, with the values saved against one
   * task — the "TaskDetail" branch Triyo.Web's Task Edit page uses. Table fields
   * come back with their Columns and Rows populated.
   */
  getTaskDetailDynamicFields: async (
    taskId: number,
    taskTypeId: number,
    projectTypeId: number
  ): Promise<TriyoDynamicField[]> => {
    const res = await apiClient.get<unknown>("/dynamic-fields", {
      params: {
        objectType: DYNAMIC_FIELD_OBJECT_TYPES.taskDetail,
        objectId: taskId,
        taskTypeId,
        projectTypeId,
      },
    });
    return toArray<TriyoDynamicField>(res.data, "DynamicFields", "dynamicFields");
  },

  /**
   * Persists dynamic-field values. For a Table field the whole grid is posted at
   * once: rows with Id 0 are inserted, known ids are updated, and any saved row
   * left out of `Table.Rows` is deleted server-side — so callers must always send
   * the complete set of rows they want to survive.
   */
  saveDynamicFields: async (fields: DynamicFieldSaveRequest[]): Promise<void> => {
    await apiClient.post("/dynamic-fields", fields);
  },

  /** Loads the base64 content for a saved File dynamic-field attachment. */
  getDynamicFormFieldFile: async (id: string): Promise<TriyoDynamicFieldFile | null> => {
    const res = await apiClient.get<unknown>("/DynamicForms/GetDynamicFormFieldFile", {
      params: { Id: id },
    });
    return res.data && typeof res.data === "object" ? (res.data as TriyoDynamicFieldFile) : null;
  },

  /**
   * Users selectable for a People Picker field, plus the resolution of any
   * already-saved selections (which may include inactive/archived users).
   * `selectedUserIds` is comma-joined, matching Triyo.Web's call.
   */
  getPeoplePickerRoster: async (
    dynamicFieldId: number,
    selectedUserIds: number[] = []
  ): Promise<PeoplePickerRoster> => {
    const res = await apiClient.get<PeoplePickerRoster>("/dynamic-fields/people-picker-roster", {
      params: { dynamicFieldId, selectedUserIds: selectedUserIds.join(",") },
    });
    return res.data && typeof res.data === "object" ? res.data : {};
  },
};
